import { useState, useEffect, useRef, useMemo } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { detectionMonitoringI18n } from './i18n';
import CardStats from '../../components/CardStats/CardStats';
import DatePickerEs from '../../components/DatePickerEs/DatePickerEs';
import {
  getMonitoringPaged,
  getPjudMonitoringClientStats,
  putPjudMonitoringClientOption,
} from '../../services/detectionMonitoringServices';
import { getAllCourts } from '../../services/courtServices';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/authService';
import './detectionMonitoring.css';
import { excelExportLogService } from 'services/excelLogFromFront';
import usePermissionsReports from 'permissions/modules/reports/usePermissionsReports';

const DetectionMonitoring = ({ DesignSystemComponents }) => {
  const { Card, Button, Input, Table, Badge, ComboBox, Dropdown } =
    DesignSystemComponents;

  const navigate = useNavigate();

  const { t, language: dmLanguage } = useScopedTranslation(detectionMonitoringI18n);

  const SORT_HUMAN_LABELS = useMemo(
    () => ({
      createAt: t('dm_col_detectionDate'),
      name: t('dm_col_client'),
      labeled: t('dm_col_cause'),
      tribunal: t('dm_col_role'),
      proc: t('dm_col_stage'),
      modificationDate: t('dm_col_update'),
    }),
    [dmLanguage]
  );

  const managedOptions = useMemo(
    () => [
      { label: t('dm_opt_all'), value: null },
      { label: t('dm_opt_managed'), value: true },
      { label: t('dm_opt_unmanaged'), value: false },
    ],
    [dmLanguage]
  );

  const defaultCourt = useMemo(
    () => ({ label: t('dm_allCourts'), value: 0 }),
    [dmLanguage]
  );

  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [isManaged, setIsManaged] = useState(false);
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [courts, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(defaultCourt);
  const [courtFilter, setCourtFilter] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detectedToday, setDetectedToday] = useState('');
  const [managedToday, setManagedToday] = useState('');
  const [totalUnmanaged, setTotalUnmanaged] = useState('');

  const [userId, setUserId] = useState(null);

  const {
    canDownloadDetectionAndMonitoringExcel,
    canManageDetectionAndMonitoring,
  } = usePermissionsReports();

  const debounceRef = useRef(null);

  const onSearchChange = (value) => {
    setSearchDraft(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 400);
  };

  const fetchCourts = async () => {
    try {
      const resp = await getAllCourts();
      setCourts([defaultCourt, ...resp]);
    } catch (error) {
      console.error('Error fetching courts', error);
    }
  };

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    setPage(0);
  };

  const formatDateForTable = (dateString) => {
    if (!dateString) return '';

    const iso = Date.parse(dateString);
    if (!isNaN(iso)) {
      const d = new Date(dateString);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }

    // eslint-disable-next-line no-useless-escape
    const ddmmyyyy = /^(\d{2})[-\/](\d{2})[-\/](\d{4})$/;
    const m1 = dateString.match(ddmmyyyy);
    if (m1) {
      const [, dd, mm, yyyy] = m1;
      return `${dd}/${mm}/${yyyy}`;
    }

    return dateString;
  };

  const formatDateForBackend = (date) => {
    if (!date) return null;

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchMonitoring = async () => {
    try {
      setTableLoading(true);

      const result = await getMonitoringPaged.getPaged({
        page: page + 1,
        pageSize,
        search,
        sortBy: sortKey,
        desc: sortDirection === 'desc',
        tribunal: courtFilter,
        startDate: startDate ? formatDateForBackend(startDate) : null,
        endDate: endDate ? formatDateForBackend(endDate) : null,
        isManaged: isManaged,
      });

      setData(result.items || []);
      setTotalItems(result.total || 0);
    } catch (e) {
      console.error('Error loading monitoring:', e);
      setData([]);
      setTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchAllMonitoring = async () => {
    const pageSizeToUse = 200;
    let currentPage = 1;
    let allItems = [];
    let total = 0;

    try {
      while (true) {
        const result = await getMonitoringPaged.getPaged({
          page: currentPage,
          pageSize: pageSizeToUse,
          search,
          sortBy: sortKey,
          desc: sortDirection === 'desc',
          tribunal: courtFilter,
          isManaged,
          startDate: startDate ? formatDateForBackend(startDate) : null,
          endDate: endDate ? formatDateForBackend(endDate) : null,
        });

        const items = result.items || [];
        total = result.total || 0;

        allItems = [...allItems, ...items];

        if (allItems.length >= total) break;

        currentPage++;
      }
    } catch (err) {
      console.error('Error fetching all data:', err);
    }

    return allItems;
  };

  const buildHumanFilters = () => {
    const filters = {};

    if (search) filters[t('dm_filterSearchBy')] = search;
    if (isManaged !== null)
      filters[t('dm_filterStatus')] = isManaged ? t('dm_opt_managed') : t('dm_opt_unmanaged');
    if (courtFilter) filters[t('dm_filterCourt')] = courtFilter;
    if (startDate && endDate)
      filters[t('dm_filterPeriod')] =
        `${formatDateForBackend(startDate)} - ${formatDateForBackend(endDate)}`;

    return filters;
  };

  const downloadExcel = async () => {
    try {
      await excelExportLogService.log({
        userId,
        source: t('dm_excelSource'),
        sortKey: sortKey ? (SORT_HUMAN_LABELS[sortKey] ?? sortKey) : null,
        sortDesc: sortDirection === 'desc' ? true : false,
        filters: buildHumanFilters(),
      });
    } catch (error) {
      console.warn('Export log failed, continuing with download', error);
    }

    setTableLoading(true);

    const allData = await fetchAllMonitoring();
    if (!allData.length) {
      setTableLoading(false);
      return;
    }

    const formatted = allData.map((item) => ({
      [t('dm_excelCol_detectionDate')]: formatDateForTable(item.createAt),
      [t('dm_excelCol_client')]: `${item.name} ${item.identifier ?? ''}`,
      [t('dm_excelCol_cause')]: item.labeled,
      [t('dm_excelCol_role')]: item.rol,
      [t('dm_excelCol_court')]: item.tribunal,
      [t('dm_excelCol_stage')]: item.proc,
      [t('dm_excelCol_update')]: formatDateForTable(item.modificationDate),
      [t('dm_excelCol_status')]: item.monitoringOptionId === null ? t('dm_status_unmanaged') : t('dm_status_managed'),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t('dm_excelSheet'));

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    });

    saveAs(blob, t('dm_excelFile'));
    setTableLoading(false);
  };

  const fetchMonitoringStatsFn = async () => {
    try {
      setLoading(true);

      const result = await getPjudMonitoringClientStats.getMonitoringStats();

      setDetectedToday(result.detectedToday);
      setManagedToday(result.managedToday);
      setTotalUnmanaged(result.totalUnmanaged);
    } catch (error) {
      console.warn('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDoesNotApply = async (item) => {
    try {
      setLoading(true);

      await putPjudMonitoringClientOption.putMonitoringOption(
        item.id,
        15,
        userId
      );

      await fetchMonitoring();
      await fetchMonitoringStatsFn();
    } catch (error) {
      console.warn('Error marking as not applicable:', error.customMessage || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoring();
    fetchMonitoringStatsFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    search,
    sortKey,
    sortDirection,
    courtFilter,
    startDate,
    endDate,
    isManaged,
  ]);

  useEffect(() => {
    fetchCourts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const authUser = getCurrentUser();
    if (authUser?.userId) {
      setUserId(authUser.userId);
    }
  }, []);

  return (
    <div>
      <div className='mb-4'>
        <div className='row'>
          <div className='col-10'>
            <h2 className='fw-semibold'>{t('dm_pageTitle')}</h2>
            <h4 className='fw-normal'>{t('dm_pageSubtitle')}</h4>
          </div>
          <div className='col-2 d-flex justify-content-end'>
            <Button
              iconStart='download'
              label={t('dm_downloadExcel')}
              variant='naked'
              size='sm'
              disabled={tableLoading || !canDownloadDetectionAndMonitoringExcel}
              onClick={downloadExcel}
            />
          </div>
        </div>
      </div>
      <div className='row mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dm_statDetectedToday')}
            count={detectedToday}
            type='info'
            icon='infoCircle'
            loading={loading}
            subtitle=''
            fill
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dm_statManagedToday')}
            count={managedToday}
            type='success'
            icon='check2Circle'
            loading={loading}
            subtitle=''
            fill
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('dm_statUnmanaged')}
            count={totalUnmanaged}
            type='warning'
            icon='clock'
            loading={loading}
            subtitle=''
          />
        </div>
      </div>
      <Card specialPadding='px-4 pt-4 pb-2'>
        <div className='row mb-3'>
          <div className='col-3'>
            <Input
              icon='search'
              placeholder={t('dm_searchPlaceholder')}
              value={searchDraft}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div className='col-3'>
            <Dropdown
              placeholder={t('dm_managedPlaceholder')}
              options={managedOptions}
              value={
                managedOptions.find((x) => x.value === isManaged) ||
                managedOptions[0]
              }
              onChange={(selected) => {
                setIsManaged(selected?.value ?? null);
                setPage(0);
              }}
            />
          </div>
          <div className='col-3'>
            <ComboBox
              placeholder={t('dm_allCourts')}
              options={courts}
              value={selectedCourt}
              onChange={(selected) => {
                setSelectedCourt(selected ?? defaultCourt);
                setCourtFilter(selected?.value === 0 ? null : selected?.label);
                setPage(0);
              }}
            />
          </div>
          <div className='col-3'>
            <DatePickerEs onChange={handleDateRangeChange} />
          </div>
        </div>
        <div className='table-container table-detection'>
          <Table
            columns={[
              {
                accessor: 'createAt',
                label: t('dm_col_detectionDate'),
                sort: true,
                render: (info) => formatDateForTable(info.createAt),
              },
              {
                accessor: 'name',
                label: t('dm_col_client'),
                sort: true,
                render: (info) => (
                  <div>
                    <p className='caption'>{info.name}</p>
                    <p className='mini'>{info.identifier}</p>
                  </div>
                ),
              },
              { accessor: 'labeled', label: t('dm_col_cause'), sort: true },
              {
                accessor: 'tribunal',
                label: t('dm_col_role'),
                sort: true,
                render: (info) => (
                  <div>
                    <p className='caption'>{info.rol}</p>
                    <p className='mini'>{info.tribunal}</p>
                  </div>
                ),
              },
              { accessor: 'proc', label: t('dm_col_stage'), sort: true },
              {
                accessor: 'modificationDate',
                label: t('dm_col_update'),
                sort: true,
                render: (info) => formatDateForTable(info.modificationDate),
              },
              {
                label: t('dm_col_status'),
                render: (info) => (
                  <Badge
                    text={
                      info.monitoringOptionId === null
                        ? t('dm_status_unmanaged')
                        : t('dm_status_managed')
                    }
                    color={
                      info.monitoringOptionId === null ? 'warning' : 'success'
                    }
                    size='sm'
                  />
                ),
              },
              {
                label: t('dm_col_actions'),
                render: (info) => (
                  <div className='d-flex'>
                    {info.monitoringOptionId === null &&
                    canManageDetectionAndMonitoring ? (
                      <Dropdown
                        ariaLabel={t('dm_aria_menu')}
                        icon='threeDotsVertical'
                        onSelect={() => {}}
                        options={[
                          {
                            label: t('dm_action_createSacTask'),
                            value: 'sac',
                          },
                          {
                            label: t('dm_action_createCause'),
                            value: 'cause',
                          },
                          {
                            label: t('dm_action_notApplicable'),
                            value: 'no',
                          },
                        ]}
                        onChange={(selected) => {
                          if (!selected) return;

                          switch (selected.value) {
                            case 'sac':
                              navigate('/tareas', {
                                state: {
                                  openTaskModal: true,
                                  preselectedTaskType: 'SAC',
                                  preselectedClient: {
                                    id: info.name,
                                    name: info.name,
                                    clientId: info.clientId,
                                  },
                                  preloadedDescription: `Rol: ${info.rol}`,
                                  fromMonitoring: true,
                                  monitoringId: info.id,
                                },
                              });
                              break;
                            case 'cause':
                              navigate('/causas', {
                                state: {
                                  fromMonitoring: true,
                                  identifier: info.identifier,
                                  monitoringId: info.id,
                                  rol: info.rol,
                                },
                              });
                              break;
                            case 'no':
                              handleDoesNotApply(info);
                              break;
                            default:
                              break;
                          }
                        }}
                        variant='icon'
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                ),
              },
            ]}
            data={data}
            page={page}
            initialPageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            paginator
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
            ]}
            paginationMode='server'
            totalItems={totalItems}
            loading={tableLoading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={({ sortKey: k, direction }) => {
              setSortKey(k);
              setSortDirection(direction);
              setPage(0);
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default DetectionMonitoring;
